// Pinned action toolbar for the ticket detail page.
//
// Problem it solves: the ticket's quick actions used to live in a big
// wrapped panel — above the thread on phones, at the top of the right rail
// on desktop — so on a long thread you scrolled back to find them. This bar
// pins just under the TopNav and is ALWAYS on screen, one line tall, at
// every width. It's the single home for the ticket actions
// (<TicketActions>: Assign, Resolve, Snooze, Escalate, Live assist, ⋯).
//
// `condensed` is driven by the parent (ticket-detail.tsx) from a scroll
// sentinel: once the full page header has scrolled out of view, the bar
// also shows the subject + status/priority/SLA (+ on-leave pill) so the
// ticket's state stays visible too. Before that the header already shows
// all of that, so the bar is just the actions, right-aligned.
//
// Reuses: `priorityColor` / `statusColor` (lib/format), `<SlaCountdown>`,
// and `<TicketActions>` (which owns every action + dialog and posts via
// `postAction`, so the dispatcher stays the single source of action logic).

import { Plane } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SlaCountdown } from '@/components/SlaCountdown';
import { TicketActions } from '@/components/TicketActions';
import type { TicketAction } from '@/lib/api';
import { buildAwayLabel, formatAwayUntil } from '@/components/AwayBadge';
import { assigneeName, priorityColor, statusColor } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { AwayPeriod, Ticket, TeamMember } from '@/lib/queries';

interface Props {
  ticket: Ticket;
  teamMembersBySlackId?: Map<string, TeamMember>;
  /** Active away periods keyed by team_member_slack_id. Passed through
   *  from `useActiveAwayPeriods()` in the parent. When the ticket's
   *  assignee is in this map, the condensed bar surfaces a compact
   *  "on leave" pill so a scrolled-down viewer doesn't have to scroll
   *  up to the full <AwayBanner> to see it. */
  awayPeriods?: Map<string, AwayPeriod>;
  /** True once the page header has scrolled out of view — adds the
   *  subject + status cluster on the left. */
  condensed?: boolean;
  /** Shown on the left while the full header is still on screen — the
   *  thread-jump pills — so the toolbar row isn't half empty. */
  leading?: React.ReactNode;
  /** Called after an action from the toolbar was started (see TicketActions). */
  onActionComplete?: (action: TicketAction) => void;
}

export function TicketStickyBar({
  ticket,
  teamMembersBySlackId,
  awayPeriods,
  condensed = false,
  leading,
  onActionComplete,
}: Props) {
  // Surface "on leave" inline when the current assignee is away. Mirrors
  // the data shape used by <AwayBanner> + <AwayDot> — same hook, same
  // resolver — so the pill stays in sync with the full banner at the top
  // of the page. Shows nothing when the ticket is unassigned or the
  // assignee isn't currently on leave.
  const awayPeriod =
    ticket.assignee_slack_id && awayPeriods
      ? awayPeriods.get(ticket.assignee_slack_id) ?? null
      : null;
  const awayName = awayPeriod
    ? (teamMembersBySlackId
        ? assigneeName(awayPeriod.team_member_slack_id, teamMembersBySlackId)
        : null) ?? awayPeriod.team_member_slack_id
    : '';
  const backupName = awayPeriod
    ? (teamMembersBySlackId
        ? assigneeName(awayPeriod.backup_slack_id, teamMembersBySlackId)
        : null) ?? awayPeriod.backup_slack_id
    : '';
  const awayLabel = awayPeriod
    ? buildAwayLabel({ awayName, backupName, endsAt: awayPeriod.ends_at })
    : '';

  return (
    <div
      role="toolbar"
      aria-label="Ticket actions"
      className={cn(
        // top-11 = TopNav height. min-w-0 so the flex children can shrink
        // instead of pushing the bar wider than the viewport.
        'sticky top-11 z-30 -mx-1 flex min-w-0 items-center justify-end gap-2 border-y border-border/70 bg-background/95 px-1 py-1.5',
        'backdrop-blur supports-[backdrop-filter]:bg-background/85',
      )}
    >
      {condensed ? (
        <div className="flex min-w-0 flex-1 items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Subject — gives context while scrolled deep into a thread. */}
          <span
            className="hidden min-w-0 flex-1 truncate text-sm font-medium text-foreground min-[400px]:block"
            title={ticket.subject ?? undefined}
          >
            {ticket.subject ?? '(no subject)'}
          </span>

          {/* Phones drop the priority badge (the letter is rarely critical
              while you're already deep in a thread) and, below 400px, the
              subject too — the status pill stays so the action cluster
              gets the room. */}
          <Badge
            variant="pill"
            className={cn('hidden sm:inline-flex', priorityColor(ticket.priority))}
          >
            {ticket.priority ?? '—'}
          </Badge>
          <Badge
            variant="pill"
            className={cn('shrink-0', statusColor(ticket.status))}
          >
            {(ticket.status ?? '—').replace(/_/g, ' ')}
          </Badge>
          <span className="hidden sm:inline-flex">
            <SlaCountdown ticket={ticket} />
          </span>

          {/* "On leave" indicator — only renders when the assignee is in
              the active away-period map. Hover/title surfaces the full
              label (who's covering + back-by). Hidden below md to keep the
              bar single-line; the full <AwayBanner> at the top of the page
              is still there for narrow viewports. */}
          {awayPeriod ? (
            <span
              title={awayLabel}
              aria-label={awayLabel}
              className="hidden items-center gap-1 rounded-full border border-tint-warning-ring bg-tint-warning px-2 py-0.5 text-2xs font-medium text-tint-warning-fg xl:inline-flex"
            >
              <Plane className="h-3 w-3" aria-hidden />
              <span className="whitespace-nowrap">
                On leave ·{' '}
                <span className="font-semibold text-tint-warning-fg">{backupName}</span>{' '}
                covering · back {formatAwayUntil(awayPeriod.ends_at)}
              </span>
            </span>
          ) : null}
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-1.5 max-[340px]:hidden">{leading}</div>
      )}

      <TicketActions ticketId={ticket.id} onActionComplete={onActionComplete} />
    </div>
  );
}
